import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import {
  Button,
  IconButton,
  ListItem,
  ListItemIcon,
  ListItemText,
  ListItemSecondaryAction,
  Box,
  ListItemAvatar,
  Avatar,
  Tooltip,
  Checkbox,
} from '@material-ui/core';
import { ShoppingCart, RemoveShoppingCart, Delete, Image } from '@material-ui/icons';
import { bindActionCreators } from 'redux';
import { connect } from 'react-redux';
import moment from 'moment';
import { Draggable } from 'react-beautiful-dnd';
import Icon from '../../../../assets/two-bars-icon.svg';
import { removeProductFromCategoryAction } from '../../../../actions/categoriesAction';
import {
  disableLocalProductForDateAction,
  disableLocalProductUntilDateAction,
  disableProductForDateAction,
  restockProductAction,
} from '../../../../actions/productsAction';
import { formatCurrency, pluralise, startsWith } from '../../../../helpers/Helpers';
import ReviewLabel from '../ReviewButton/ReviewLabel';
import useStyles from './ProductStyles';

const MenuProduct = ({
  product,
  removeProductFromCategory,
  disableProductForDate,
  index,
  selected,
  onSelect,
  category,
  menuVersion,
  disabled,
  restockProduct,
  disableLocalProductForDate,
  disableLocalProductUntilDate,
  hideImages, // To not show images for performance reasons
  setOpenDialog,
}) => {
  const classes = useStyles();
  const [isDisabled, setIsDisabled] = useState(
    moment(product.disableDate, 'yyyy-MM-DD').isSame(moment(), 'day') ||
      moment(product.disableUntilDate, 'yyyy-MM-DD').isAfter(moment(), 'day'),
  );

  useEffect(() => {
    setIsDisabled(
      moment(product.disableDate, 'yyyy-MM-DD').isSame(moment(), 'day') ||
        moment(product.disableUntilDate, 'yyyy-MM-DD').isAfter(moment(), 'day'),
    );
  }, [product, product.disableDate, product.disableUntilDate]);

  const handleDelete = () => {
    removeProductFromCategory(product.objectId, category.objectId);
  };

  const toggleIsDisabled = () => {
    if (isDisabled) {
      if (startsWith(product.objectId, 'product_')) {
        disableLocalProductForDate(product.objectId, null);
        disableLocalProductUntilDate(product.objectId, null);
      } else {
        restockProduct(product.objectId);
      }
      return;
    }

    const newDisableDate = moment().format('yyyy-MM-DD');
    if (startsWith(product.objectId, 'product_')) {
      disableLocalProductForDate(product.objectId, newDisableDate);
    } else {
      disableProductForDate(product.objectId, newDisableDate);
    }
  };

  return (
    <Draggable draggableId={`${category.objectId} ${product.objectId}`} index={index}>
      {(provided, snapshot) => (
        <Box
          ref={provided.innerRef}
          {...provided.draggableProps}
          className={`${classes.draggableProduct} ${snapshot.isDragging ? 'dragging' : ''} ${
            snapshot.isDropAnimating ? 'animating' : ''
          }`}
          boxShadow={snapshot.isDragging ? 3 : 0}
        >
          <ListItem button onClick={() => setOpenDialog(true)}>
            <Checkbox
              style={{ color: disabled && 'rgba(0, 0, 0, 0.26)' }}
              color='primary'
              checked={selected}
              onClick={(e) => e.stopPropagation()}
              onChange={(e) => !disabled && onSelect(e.target.checked)}
            />
            <Tooltip title='Drag to re-order'>
              <ListItemIcon {...provided.dragHandleProps}>
                <Icon style={{ fill: 'rgba(0, 0, 0, 0.54)' }} />
              </ListItemIcon>
            </Tooltip>
            <ListItemAvatar>
              {!hideImages && product.imageLink ? (
                <Avatar alt='Image' src={product.imageLink} />
              ) : (
                <Avatar>
                  <Image />
                </Avatar>
              )}
            </ListItemAvatar>
            <ListItemText
              primary={
                <Box display='flex' gridColumnGap='20px'>
                  <span>{product.productTitle}</span>
                  <ReviewLabel importStatus={product?.importStatus} />
                </Box>
              }
              secondary={hideImages ? '' : product?.productDescription}
              style={{ paddingRight: menuVersion.posEnabled ? '569px' : '404px' }}
            />
            <ListItemSecondaryAction style={{ pointerEvents: 'none' }}>
              <Box display='flex' gridColumnGap='15px' style={{ pointerEvents: 'none' }}>
                <Box
                  style={{
                    padding: '20px 0',
                    textAlign: 'left',
                    width: '100px',
                  }}
                >
                  {product?.optionIds?.length > 0 &&
                    `${product?.optionIds?.length} ${pluralise(
                      'option',
                      product?.optionIds?.length,
                    )}`}
                </Box>
                <span
                  style={{
                    padding: '20px 0',
                    textAlign: 'left',
                    width: '100px',
                  }}
                >
                  {formatCurrency(product?.price)}
                </span>
                <Button
                  variant='text'
                  size='medium'
                  color={isDisabled ? 'primary' : 'default'}
                  startIcon={isDisabled ? <RemoveShoppingCart /> : <ShoppingCart />}
                  className={`${classes.listButton} ${isDisabled ? 'listButtonDisabled' : ''}`}
                  onClick={toggleIsDisabled}
                  title='Toggle stock level'
                  style={{
                    fontSize: isDisabled ? '12px' : '14px',
                    pointerEvents: 'auto',
                  }}
                >
                  {isDisabled ? 'Out of Stock' : 'In Stock'}
                </Button>

                <Tooltip title='Remove item from menu'>
                  <IconButton
                    edge='end'
                    aria-label='delete'
                    onClick={handleDelete}
                    style={{
                      pointerEvents: 'auto',
                    }}
                  >
                    <Delete />
                  </IconButton>
                </Tooltip>
              </Box>
            </ListItemSecondaryAction>
          </ListItem>
        </Box>
      )}
    </Draggable>
  );
};

MenuProduct.defaultProps = {
  hideImages: false,
};

MenuProduct.propTypes = {
  product: PropTypes.object.isRequired,
  hideImages: PropTypes.bool,
};

const mapStateToProps = (state) => ({
  menuVersion: state.menuVersion,
});

const mapDispatchToProps = (dispatch) =>
  bindActionCreators(
    {
      removeProductFromCategory: removeProductFromCategoryAction,
      disableProductForDate: disableProductForDateAction,
      restockProduct: restockProductAction,
      disableLocalProductForDate: disableLocalProductForDateAction,
      disableLocalProductUntilDate: disableLocalProductUntilDateAction,
    },
    dispatch,
  );

export default connect(mapStateToProps, mapDispatchToProps)(MenuProduct);
